import { toPng } from 'html-to-image'
import { useEffect, useMemo, useState } from "react"
import { ReactSortable } from "react-sortablejs"
import api from "../../api"
import { ReactComponent as ChevronIcon } from "../../assets/images/icons/ic-chevron.svg"
import { ReactComponent as EditIcon } from "../../assets/images/icons/ic-edit.svg"
import { ReactComponent as EyeOffIcon } from "../../assets/images/icons/ic-eye-closed.svg"
import { ReactComponent as EyeIcon } from "../../assets/images/icons/ic-eye.svg"
import { ReactComponent as TemplateIcon } from "../../assets/images/icons/ic-template.svg"
import { BlockType } from "../../common/constants"
import { Each } from "../../common/Each"
import useContextMenu from "../../common/hooks/useContextMenu"
import Button from "../Button"
import Dialog from "../dialogs/Dialog"
import TextInput from "../TextInput"
import styles from "./PageBuilderStructure.module.css"
import { usePageBuilder } from './PageBuilderContext'

const PageBuilderStructure = ({ view, isPreviewMode = false }) => {

    const {page, setPage, selectedBlockId, selectedBlockIds, selectBlock, updateBlock, toggleHideBlock } = usePageBuilder();
    const [expandedItems, setExpandedItems] = useState([])
    const { showMenu } = useContextMenu()
    const [renameDialogOpen, setRenameDialogOpen] = useState(null)
    const [templateDialog, setTemplateDialog] = useState(null)
    const [newName, setNewName] = useState(null)

    useEffect(() => {
        if (selectedBlockId) {
            const path = findPath(page, selectedBlockId)
            if (path) {
                setExpandedItems(path)
            }
        }
    }, [selectedBlockId])

    const findPath = (items, id, path = []) => {
        for (let item of items) {
            if (item.id === id) {
                return [...path, item.id]
            }
            if (item.content && item.content.length > 0) {
                const result = findPath(item.content, id, [...path, item.id])
                if (result) {
                    return result
                }
            }
        }
        return null
    }

    const handleExpand = (id) => {
        if (expandedItems.includes(id)) {
            setExpandedItems(expandedItems.filter(item => item !== id))
        }
        else {
            setExpandedItems([...expandedItems, id])
        }
    };

    const getViewDisplay = (item) => {
        switch (view) {
            case 'desktop':
                return item.style?.display
            case 'tablet':
                return item.styleTablet?.display ?? item.style?.display
            case 'mobile':
                return item.styleMobile?.display ?? item.styleTablet?.display ?? item.style?.display
            default:
                return item.style?.display
        }
    }

    const showContextMenu = (e, item) => {
        e.stopPropagation()
        e.preventDefault()
        showMenu(e, [
            {
                name: "Rinomina",
                icon: <EditIcon style={{ color: "var(--sf-green)" }} />,
                onClick: () => {
                    setNewName(item.name)
                    setRenameDialogOpen(item)
                },
            },
            {
                name: "Crea Template",
                icon: <TemplateIcon style={{ color: "var(--sf-purple)" }} />,
                onClick: async () => {
                    const node = document.getElementById(item.id);

                    const dataUrl = await toPng(node, { cacheBust: true })
                    const blob = await fetch(dataUrl).then(res => res.blob());
                    const url = URL.createObjectURL(blob);
                    console.log("dataUrl: ", url)
                    setTemplateDialog({ item, thumbnail: url, dataUrl })

                },
            },
        ]);
    }

    const normalizedPage = Array.isArray(page) ? page : []
    const invalidBlocks = useMemo(() => collectInvalidBlocks(normalizedPage), [normalizedPage])
    const hasInvalidStructure = page && !Array.isArray(page)
    const invalidIdsSet = useMemo(() => new Set(invalidBlocks.map((block) => block.id)), [invalidBlocks])
    const invalidAncestorsSet = useMemo(() => {
        const ancestors = new Set()
        invalidBlocks.forEach((block) => {
            const path = findPath(normalizedPage, block.id) || []
            path.slice(0, -1).forEach((id) => ancestors.add(id))
        })
        return ancestors
    }, [invalidBlocks, normalizedPage])


    const createTemplate = async () => {
        try {

            const formData = new FormData();
            const file = await dataUrlToFile(templateDialog.dataUrl, `${templateDialog.name || 'template'}.png`, 'image/png');
            formData.append('thumbnail', file);
            formData.append('content', JSON.stringify(templateDialog.item));
            formData.append('name', templateDialog.name);

            await api.post(`/admin/builder/templates`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            setTemplateDialog(null)
        }
        catch (e) {
            console.log(e)
        }
    }

    const dataUrlToFile = async (dataUrl, fileName, mimeType = 'image/png') => {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return new File([blob], fileName, { type: mimeType });
    };

    const findFirstInvalidDescendant = (item) => {
        if (!item || !Array.isArray(item.content)) {
            return null
        }
        for (const child of item.content) {
            if (invalidIdsSet.has(child.id)) {
                return child.id
            }
            const nested = findFirstInvalidDescendant(child)
            if (nested) {
                return nested
            }
        }
        return null
    }

    const renderContent = (content) => {
        return (
            <Each of={Array.isArray(content) ? content : []} render={(item, index) => {
                const contentIsInvalid = item.content && !Array.isArray(item.content)
                const hasInvalidDescendant = invalidAncestorsSet.has(item.id)
                const descendantInvalidId = hasInvalidDescendant ? findFirstInvalidDescendant(item) : null
                const headerClasses = [styles.header]
                if (Array.isArray(selectedBlockIds) && selectedBlockIds.includes(item.id)) headerClasses.push(styles.selected)
                if (contentIsInvalid) headerClasses.push(styles.invalid)
                else if (hasInvalidDescendant) headerClasses.push(styles.invalidAncestor)
                return (
                    <div className={styles.item} onContextMenu={(e) => { showContextMenu(e, item) }}>
                        <div className={headerClasses.join(' ')}
                            style={{ opacity: getViewDisplay(item) === 'none' ? 0.5 : 1 }}
                            onClick={(event) => {
                                if (isPreviewMode) {
                                    return
                                }
                                const isCmdOrCtrl = event.metaKey || event.ctrlKey
                                const isCmdShift = isCmdOrCtrl && event.shiftKey
                                if (isCmdOrCtrl && !event.shiftKey) {
                                    selectBlock(item.id)
                                    return
                                }
                                selectBlock(item.id, {
                                    range: event.shiftKey && !isCmdOrCtrl,
                                    additive: isCmdShift,
                                    addOnly: isCmdShift,
                                })
                            }}>
                            {item.name ? `${item.name} (${item.type})` : `${item.type} ${item.type === BlockType.Section ? index + 1 : ''}`}
                            <div className={styles.name}>
                                {contentIsInvalid && (
                                    <button
                                        type="button"
                                        className={styles.invalidBadge}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (isPreviewMode) {
                                                return
                                            }
                                            selectBlock(item.id)
                                            setExpandedItems((prev) => prev.includes(item.id) ? prev : [...prev, item.id])
                                        }}
                                    >
                                        Contenuto non valido
                                    </button>
                                )}
                                {!contentIsInvalid && hasInvalidDescendant && descendantInvalidId && (
                                    <button
                                        type="button"
                                        className={styles.invalidBadge}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (isPreviewMode) {
                                                return
                                            }
                                            selectBlock(descendantInvalidId)
                                            const path = findPath(normalizedPage, descendantInvalidId)
                                            if (path) {
                                                setExpandedItems(path)
                                            }
                                        }}
                                    >
                                        CONTENUTO NON VALIDO
                                    </button>
                                )}

                                {getViewDisplay(item) === 'none' ?
                                    <EyeOffIcon className={styles.hideIcon} onClick={(e) => {
                                        e.stopPropagation()
                                        toggleHideBlock(item.id, view)
                                    }} />
                                    :
                                    <EyeIcon className={styles.hideIcon} onClick={(e) => {
                                        e.stopPropagation()
                                        toggleHideBlock(item.id, view)
                                    }} />
                                }
                                <ChevronIcon className={`${styles.chevron} ${expandedItems.includes(item.id) ? styles.expanded : ''}`} style={{ visibility: item.content && item.content.length > 0 ? 'visible' : 'hidden' }} onClick={(e) => {
                                    if (!item.content || item.content.length === 0) return
                                    e.stopPropagation()
                                    handleExpand(item.id)
                                }} />
                            </div>
                        </div>
                        {contentIsInvalid ? (
                            <div className={`${styles.content} ${expandedItems.includes(item.id) ? styles.expanded : ''}`}>
                                <div className={styles.invalidContent}>
                                    Struttura contenuto non valida. Atteso un array, trovato "{typeof item.content}".
                                </div>
                            </div>
                        ) : (
                            <ReactSortable
                                list={item.content || []}
                                setList={(orderedContent) => {
                                    let newPage = [...page];
                                    const findAndUpdateContent = (items) => {
                                        return items.map(i => {
                                            if (i.id === item.id) {
                                                return { ...i, content: orderedContent }
                                            }
                                            if (i.content && i.content.length > 0) {
                                                return { ...i, content: findAndUpdateContent(i.content) }
                                            }
                                            return i;
                                        })
                                    }
                                    newPage = findAndUpdateContent(newPage)
                                    setPage(newPage)
                                }}
                                className={`${styles.content} ${expandedItems.includes(item.id) ? styles.expanded : ''}`}
                            >
                                {renderContent(item.content)}
                            </ReactSortable>
                        )}
                    </div>
                )
            }} />
        )
    }

    return (
        <>
            {hasInvalidStructure && (
                <div className={styles.invalidSection}>
                    <strong>Struttura pagina non valida.</strong> Atteso un array di blocchi, trovato "{typeof page}".
                </div>
            )}
            {invalidBlocks.length > 0 && (
                <div className={styles.invalidSection}>
                    <strong>{invalidBlocks.length} blocc{invalidBlocks.length === 1 ? 'o' : 'hi'} non valido:</strong>
                    <ul>
                        {invalidBlocks.map((block) => (
                            <li key={block.id}>
                                <button
                                    type="button"
                                    className={styles.invalidLink}
                                    onClick={() => {
                                        if (isPreviewMode) {
                                            return
                                        }
                                        selectBlock(block.id)
                                        const path = findPath(normalizedPage, block.id)
                                        if (path) {
                                            setExpandedItems(path)
                                        }
                                    }}
                                >
                                    {block.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <ReactSortable list={normalizedPage} setList={(newPage) => {
                setPage(newPage)
            }} className={styles.container}>
                {renderContent(normalizedPage)}
            </ReactSortable>
            <Dialog
                action={
                    <Button onClick={() => {
                        updateBlock(renameDialogOpen.id, { name: newName })
                        setNewName(null)
                        setRenameDialogOpen(false)
                    }}>RINOMINA</Button>

                }
                open={renameDialogOpen} title="Rinomina Elemento" onClose={() => setRenameDialogOpen(false)}>
                <TextInput type="text" placeholder={"Nome blocco"} value={newName ?? ""} onKeyUp={(value) => {
                    setNewName(value)
                }} />
            </Dialog>
            {templateDialog &&
                <Dialog
                    action={
                        <Button onClick={() => {
                            createTemplate()
                        }}>SALVA TEMPLATE</Button>

                    }
                    open={templateDialog} title="Template" onClose={() => setTemplateDialog(false)}>
                    <img src={templateDialog.thumbnail} className={styles.templateThumbnail} alt="" />
                    <TextInput type="text" placeholder={"Nome template"} value={newName ?? ""} onKeyUp={(value) => {
                        setTemplateDialog((prev) => { return { ...prev, name: value } })
                    }} />
                </Dialog>
            }
        </>
    )

}

const collectInvalidBlocks = (items) => {
    if (!Array.isArray(items)) {
        return []
    }
    return items.reduce((acc, item) => {
        if (!item) {
            return acc
        }
        if (item.content && !Array.isArray(item.content)) {
            acc.push({
                id: item.id,
                label: item.name || item.type || item.id
            })
        }
        if (Array.isArray(item.content) && item.content.length > 0) {
            acc.push(...collectInvalidBlocks(item.content))
        }
        return acc
    }, [])
}

export default PageBuilderStructure
